import React from "react";
import Framework from "@beardeddevops/react.framework";
import Types from "constants/Types";
import PropTypes from "prop-types";
import ObjectOverride from "components/ObjectOverride";
import API from "@beardeddevops/react.api";

/**
 *  @property {Customers_Emails_Object} state
 *  @property {object} model
 *  @property {Customers_Emails_Properties} model.properties
 *  @property {Customers_Emails_Is} model.is
 *  @property {Customers_Emails_Has} model.has
 */
export default class SendMyFFL extends ObjectOverride {
	static propTypes = {
		...super.PropTypes,
		email: PropTypes.object,
	};

	constructor(props) {
		super(props, Types.Locations._name);
		this.update = 'POST_SEND';
		this.model.response_message = 'Email Sent';
		this.model.properties.send_email_to.required = true;
		this.handleProcess = this.handleProcess.bind(this);

		// Add new state variables for preview
		this.state = {
			...this.state,
			previewLoading: false,
			showPreview: false,
			previewHtml: '',
			previewError: '',
			isEditing: false
		};

		// Create a ref for the iframe
		this.iframeRef = React.createRef();
	}

	handleProcess(event, requestOverride) {
		let request = typeof requestOverride !== 'undefined' && requestOverride ? requestOverride : this.requestType;
		return API.Validator.processItem(this, 'send_email_to', request);
	}

	/**
	 * Fetch email preview from backend
	 */
	handlePreview = async () => {
		// Validate form first
		const validationResult = API.Validator.processItem(this, 'send_email_to', 'validate');

		if (!validationResult) {
			return; // Form has validation errors
		}

		this.setState({ previewLoading: true });

		// Call the preview endpoint
		let previewModel = new API.BuildClass(Types.Locations._name, 'POST_PREVIEW_EMAIL');
		previewModel.object.send_email_to = this.state.send_email_to;
		previewModel.object.send_message = this.state.send_message;
		let response = await previewModel.submit(this, 'POST_PREVIEW_EMAIL');

		this.setState({
			previewHtml: response.preview,
			showPreview: true,
			previewLoading: false,
			previewError: '',
			isEditing: false
		});
	};

	/**
	 * Close the preview modal
	 */
	closePreview = () => {
		this.setState({ showPreview: false });
	};

	/**
	 * Enable editing mode
	 */
	enableEditing = () => {
		this.setState({ isEditing: true }, () => {
			// Initialize the iframe content after state update
			setTimeout(this.initializeIframe, 100);
		});
	};

	/**
	 * Initialize iframe with editable content
	 */
	initializeIframe = () => {
		if (!this.iframeRef.current) return;

		const iframe = this.iframeRef.current;
		const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

		// Set iframe document content
		iframeDoc.open();

		// Create HTML with editing styles
		const htmlContent = `
			<!DOCTYPE html>
			<html>
			<head>
				<style>
					body {
						font-family: Arial, sans-serif;
						padding: 10px;
						margin: 0;
						min-height: 300px;
					}
					[contenteditable] {
						outline: none;
					}
					.editable-highlight {
						background-color: rgba(255, 255, 0, 0.1);
						padding: 2px;
						border-radius: 2px;
					}
					/* Style for hover on editable areas */
					[contenteditable]:hover {
						background-color: rgba(135, 206, 250, 0.1);
						border-radius: 2px;
					}
					/* When element has focus */
					[contenteditable]:focus {
						background-color: rgba(135, 206, 250, 0.2);
						border-radius: 2px;
					}
				</style>
			</head>
			<body contenteditable="true">
				${this.state.previewHtml}
			</body>
			</html>
		`;

		iframeDoc.write(htmlContent);
		iframeDoc.close();

		// Make sure iframe has focus and cursor is visible
		setTimeout(() => {
			try {
				iframe.contentWindow.focus();
				// Try to put cursor at the beginning of the body
				const range = iframeDoc.createRange();
				const selection = iframe.contentWindow.getSelection();
				const bodyElement = iframeDoc.body;

				if (bodyElement.firstChild) {
					range.setStart(bodyElement.firstChild, 0);
					range.collapse(true);
					selection.removeAllRanges();
					selection.addRange(range);
				}
			} catch (e) {
				console.error("Error setting focus in iframe:", e);
			}
		}, 200);
	};

	/**
	 * Save the edited content from iframe
	 */
	saveEdits = () => {
		if (!this.iframeRef.current) return;

		const iframe = this.iframeRef.current;
		const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

		// Get the HTML content from the iframe's body
		const editedContent = iframeDoc.body.innerHTML;

		this.setState({
			previewHtml: editedContent,
			isEditing: false
		});
	};

	/**
	 * Cancel editing and revert changes
	 */
	cancelEditing = () => {
		this.setState({ isEditing: false });
	};

	/**
	 * Override handleSubmit to use the edited preview HTML
	 */
	handleSubmit = async (event) => {
		// Close the preview if it's open
		if (this.state.showPreview) {
			// Create a new API call that will send the edited HTML directly
			let sendModel = new API.BuildClass(Types.Locations._name, 'POST_SEND_EDITED');
			sendModel.object.send_email_to = this.state.send_email_to;
			sendModel.object.send_message = this.state.send_message;
			sendModel.object.edited_html = this.state.previewHtml;

			this.setState({ loading: true });

			let response = await sendModel.submit(this, 'POST_SEND_EDITED');
			this.setState({
				loading: false,
				showPreview: false
			});

			this.props.afterSave();

		} else {
			// Use the original submit if preview isn't open
			super.handleSubmit(event);
		}
	};

	/**
	 * Render preview modal
	 */
	renderPreviewModal = () => {
		if (!this.state.showPreview) return null;

		const { isEditing, previewHtml } = this.state;

		return (
			<div className="modal preview-modal" tabIndex="-1" role="dialog" style={{ display: 'block' }}>
				<div className="modal-dialog modal-lg" role="document">
					<div className="modal-content">
						<div className="modal-header">
							<h5 className="modal-title">
								Email Preview {isEditing ? "(Editing Mode)" : ""}
							</h5>
							<button type="button" className="close" onClick={this.closePreview}>
								<span aria-hidden="true">&times;</span>
							</button>
						</div>
						<div className="modal-body" style={{ minHeight: '500px' }}>
							{isEditing ? (
								<div className="iframe-container" style={{ height: '900px' }}>
									<iframe
										ref={this.iframeRef}
										style={{
											width: '100%',
											height: '100%',
											border: '1px solid #ddd',
											borderRadius: '4px'
										}}
										title="Email Editor"
									/>
								</div>
							) : (
								<div
									className="email-preview-content"
									dangerouslySetInnerHTML={{ __html: previewHtml }}
								/>
							)}
						</div>
						<div className="modal-footer">
							{isEditing ? (
								<>
									<button
										type="button"
										className="btn btn-outline-secondary mr-2"
										onClick={this.cancelEditing}
									>
										Cancel
									</button>
									<button
										type="button"
										className="btn btn-success mr-2"
										onClick={this.saveEdits}
									>
										Save Changes
									</button>
								</>
							) : (
								<>
									<button
										type="button"
										className="btn btn-outline-primary mr-2"
										onClick={this.enableEditing}
									>
										Edit Email
									</button>
									<button
										type="button"
										className="btn btn-secondary mr-2"
										onClick={this.closePreview}
									>
										Close
									</button>
									<button
										type="button"
										className="btn btn-primary"
										onClick={this.handleSubmit}
									>
										Send Email
									</button>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	};

	form = () => {
		/** @type {Locations_Properties} properties */
		let properties = this.model.properties;
		/** @type {Locations_Object} object */
		let object = this.state;
		let { loading, previewLoading, previewError } = this.state;

		return (
			<div>
				<fieldset>
					<h4 className="section-title">Email</h4>
					<div className="form-group">
						<Framework.Elements.EmailAddress
							property={properties.send_email_to}
							value={object.send_email_to}
							error={object.send_email_to_error}
							update={this.handleTextFieldPropertyUpdate}
						/>
					</div>

					<h4 className="section-title">Message</h4>
					<div className="form-group">
						<Framework.Elements.TextField
							multiline={4}
							property={properties.send_message}
							value={object.send_message}
							error={object.send_message_error}
							update={this.handleTextFieldPropertyUpdate}
						/>
					</div>

					{previewError && (
						<div className="alert alert-danger" role="alert">
							{previewError}
						</div>
					)}

					<div className="form-group mt-4">
						<button
							type="button"
							className="btn btn-outline-primary mr-2"
							disabled={loading || previewLoading}
							onClick={this.handlePreview}
						>
							{previewLoading ? 'Loading Preview...' : 'Preview Email'}
						</button>
						
					</div>
				</fieldset>

				<Framework.Elements.LoadingSpinner isLoading={loading || previewLoading}/>
				{this.renderPreviewModal()}
			</div>
		)
	};
}